<script lang="ts">
  import { onDestroy, onMount } from "svelte";
  import { fetchDarkpoolLevels, type DarkpoolLevelsResponse, type DarkpoolRailReceipt, type VenueState } from "./api";
  import { darkpoolRailModel, darkpoolRefusalLines, servedAgeS, type DarkpoolRailModel } from "./darkpoolRail";
  import { reportDoorFailure, servedClosure, type DoorFaultEvidence } from "./faultEvidence";
  import PanelFreshness from "./PanelFreshness.svelte";
  import PanelInfo from "./PanelInfo.svelte";
  import AgePill from "./AgePill.svelte";
  import { keptPayloadAfterFailure } from "./dataAge";

  let response = $state<DarkpoolLevelsResponse | null>(null);
  let responseFetchedAtMs = $state<number | null>(null);
  let responseRequestKey = $state<string | null>(null);
  let model = $state<DarkpoolRailModel | null>(null);
  let served = $state<string | null>(null);
  let ageS = $state<number | null>(null);
  let budgetS = $state(600);
  let servedStale = $state<boolean | null>(null);
  let venueState = $state<VenueState | null>(null);
  let fault = $state<DoorFaultEvidence | null>(null);
  let retainedClockMs = $state<number | null>(null);
  let nowMs = $state(Date.now());
  let receivedAtMs = $state(Date.now());
  let loaded = $state(false);
  let infoOpen = $state(false);
  let timer: ReturnType<typeof setInterval> | null = null;
  let clock: ReturnType<typeof setInterval> | null = null;
  let lastRefusalLog = "";
  let gen = 0;

  // A body the model refuses must still render its refusal: never read rails the door did not serve as an object.
  function railEntries(body: DarkpoolLevelsResponse | null): [string, DarkpoolRailReceipt][] {
    const rails = body?.rails;
    if (rails === null || typeof rails !== "object" || Array.isArray(rails)) return [];
    return Object.entries(rails).filter((entry): entry is [string, DarkpoolRailReceipt] =>
      entry[1] !== null && typeof entry[1] === "object");
  }

  async function load() {
    const mine = ++gen;
    const requestKey = "/api/darkpool/levels?ticker=SPY";
    try {
      const body = await fetchDarkpoolLevels();
      if (mine !== gen) return;
      response = body;
      responseFetchedAtMs = Date.now();
      responseRequestKey = requestKey;
      const next = darkpoolRailModel(body);
      model = next;
      receivedAtMs = Date.now();
      nowMs = receivedAtMs;
      // A refusal served in a 200 is drawn below AND logged here, once per change (GexGridView's form).
      const lines = darkpoolRefusalLines(next);
      if (lines.join("\n") !== lastRefusalLog) {
        for (const line of lines) console.error(line);
        lastRefusalLog = lines.join("\n");
      }
      served = typeof body.root === "string" ? body.root : null;
      ageS = typeof body.age_s === "number" && Number.isFinite(body.age_s) ? body.age_s : null;
      budgetS = typeof body.budget_s === "number" && Number.isFinite(body.budget_s) ? body.budget_s : 600;
      servedStale = body.stale;
      venueState = body.venue_state ?? null;
      const receipts = railEntries(body).map(([, rail]) => Date.parse(rail.newest_receipt_ts ?? ""));
      const newest = receipts.reduce((latest, stamp) => Number.isFinite(stamp) ? Math.max(latest, stamp) : latest, Number.NEGATIVE_INFINITY);
      retainedClockMs = Number.isFinite(newest) ? newest : Date.now();
      fault = null;
    } catch (error) {
      if (mine !== gen) return;
      nowMs = Date.now();
      fault = reportDoorFailure("darkpool", error, retainedClockMs, nowMs);
      response = keptPayloadAfterFailure(response, responseRequestKey, requestKey);
      if (response === null) {
        model = null;
        responseFetchedAtMs = null;
        responseRequestKey = null;
      }
    } finally {
      if (mine === gen) loaded = true;
    }
  }

  onMount(() => {
    void load();
    timer = setInterval(load, 120_000);
    clock = setInterval(() => (nowMs = Date.now()), 1_000);
  });
  onDestroy(() => {
    gen += 1;
    if (timer !== null) clearInterval(timer);
    if (clock !== null) clearInterval(clock);
  });

  let venueClosed = $derived(servedClosure(venueState));
  let liveAgeS = $derived(servedAgeS(ageS, receivedAtMs, nowMs));
  let stale = $derived(venueClosed === null && (servedStale === true || (liveAgeS !== null && liveAgeS > budgetS)));
  let freshnessTitle = $derived(
    liveAgeS === null ? "dark-pool receipt age unserved" : `dark-pool receipt age ${Math.round(liveAgeS)}s · budget ${budgetS}s`,
  );
  const shortDate = (date: string) => /^\d{4}-\d{2}-\d{2}$/.test(date) ? date.slice(5) : date;
</script>

<section class="darkpool-rail" aria-label="DARK POOL">
  <header class="panel-id">
    <h3>DARK POOL</h3>
    <span class="pill">{served ?? "—"}</span>
    <span class="chip hyp">HYPOTHESIS</span>
    <PanelFreshness label="dark pool" {venueState} {stale} ageTitle={freshnessTitle} />
    <AgePill payload={response} fetchedAtMs={responseFetchedAtMs} door="/api/darkpool/levels?ticker=SPY" root={served} />
    <PanelInfo id="darkpool-info" label="Dark pool information" open={infoOpen} ontoggle={() => (infoOpen = !infoOpen)}>
      <p>{response?.cluster_basis ?? "cluster basis UNSERVED"}</p>
      <p>{response?.age_basis ?? "age basis UNSERVED"}</p>
      {#each railEntries(response) as [endpoint, rail]}
        <p>{endpoint} · served_trading_date {rail.served_trading_date ?? "UNSERVED"} · newest_receipt_ts {rail.newest_receipt_ts ?? "UNSERVED"}</p>
      {/each}
      <p>SPY units. Never an ES or SPX level; no x10 conversion.</p>
      <p>source tables uw_darkpool_price_levels_v2 + uw_stock_volume_price_levels_v2</p>
    </PanelInfo>
  </header>

  {#if fault}
    <p class="msg" role="alert"><b>DOOR FAILURE</b> · {fault.label}{#if fault.retained} · {fault.retained}{/if}</p>
  {/if}
  {#if !loaded}
    <p class="loading" aria-label="loading dark pool">●</p>
  {:else if model?.ok === false}
    <p class="msg" role="alert"><b>{model.refused}</b><br /><small>{model.detail}</small></p>
  {:else if model?.ok === true}
    <div class="columns">
      {#each model.columns as column}
        <div class="column">
          <div class="column-head">
            <b>{column.title}</b>
            {#if column.ok}<span>{shortDate(column.servedTradingDate)}</span>{:else}<span class="refused">{column.refused}</span>{/if}
          </div>
          {#if column.ok}
            {#each column.rows as row}
              <div class="level">
                <span class="price">{row.price}</span>
                <span class="bar" aria-label={`${row.price}: off ${row.off}, other ${row.other}, total ${row.total}`}>
                  <span class="off" style:width={`${column.max > 0 ? row.off / column.max * 100 : 0}%`}></span>
                  <span class="other" style:width={`${column.max > 0 ? row.other / column.max * 100 : 0}%`}></span>
                </span>
              </div>
            {/each}
          {/if}
        </div>
      {/each}
    </div>
  {:else}
    <p class="msg" role="alert"><b>PAYLOAD_INVALID</b></p>
  {/if}
</section>

<style>
  .darkpool-rail { display: flex; flex-direction: column; gap: 5px; min-width: 0; }
  .panel-id { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
  .panel-id h3 { margin: 0; font-size: 12px; letter-spacing: .08em; color: var(--ink); }
  .pill { display: inline-flex; align-items: center; border: 1px solid var(--line); border-radius: var(--radius-pill); padding: 2px 7px; background: var(--chip); color: var(--muted); font-size: 10px; line-height: 1; }
  .chip { font: 10px var(--font-num); color: var(--muted); border: 1px solid var(--line); border-radius: var(--radius-chip); padding: 1px 6px; }
  .columns { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); gap: 10px; }
  .column { min-width: 0; }
  .column-head { display: flex; justify-content: space-between; gap: 5px; margin-bottom: 2px; color: var(--muted); font: 10px var(--font-ui); }
  .column-head b { color: var(--ink); letter-spacing: .05em; }
  .refused { color: var(--stale-ink); }
  .level { display: grid; grid-template-columns: 60px minmax(0, 1fr); align-items: center; gap: 5px; height: 12px; font: 10px/12px var(--font-num); }
  .price { color: var(--ink); text-align: right; }
  .bar { display: flex; height: 6px; overflow: hidden; border-radius: var(--radius-chip); background: var(--chip); }
  /* Volume has no direction: the neutral accent, never the up/dn pair. */
  .off { display: block; background: var(--accent); }
  .other { display: block; background: var(--accent); opacity: .28; }
  .msg { margin: 0; padding: 8px 0; color: var(--muted); }
  .loading { margin: 0; padding: 8px 0; color: var(--faint); }
  @media (max-width: 520px) { .columns { gap: 6px; } .level { grid-template-columns: 52px minmax(0, 1fr); } }
</style>
